/**
 * OPS-11: Service Impact Tags
 *
 * Maps alert/risk types to hotel service impact categories.
 * Used by NeedsAttentionPanel, AlertsPanel, and OperationalTimeline
 * to show which business services are affected by operational issues.
 *
 * Pure frontend mapping — no backend changes required.
 */

// ─── Impact Categories ───────────────────────────────────────────────────────

export type ServiceImpact =
  | 'ROOM READINESS'
  | 'HOUSEKEEPING'
  | 'ROOM SERVICE'
  | 'GUEST EXPERIENCE'
  | 'MAINTENANCE'
  | 'FRONT DESK'
  | 'OPERATIONS';

const IMPACT_STYLES: Record<ServiceImpact, { bg: string; text: string }> = {
  'ROOM READINESS':    { bg: 'bg-violet-500/15', text: 'text-violet-400' },
  'HOUSEKEEPING':      { bg: 'bg-cyan-500/15',   text: 'text-cyan-400' },
  'ROOM SERVICE':      { bg: 'bg-amber-500/15',  text: 'text-amber-400' },
  'GUEST EXPERIENCE':  { bg: 'bg-pink-500/15',   text: 'text-pink-400' },
  'MAINTENANCE':       { bg: 'bg-orange-500/15', text: 'text-orange-400' },
  'FRONT DESK':        { bg: 'bg-sky-500/15',    text: 'text-sky-400' },
  'OPERATIONS':        { bg: 'bg-slate-500/15',  text: 'text-slate-400' },
};

// ─── Type → Impact Mapping ───────────────────────────────────────────────────

const TYPE_IMPACT_MAP: [RegExp, ServiceImpact][] = [
  [/HOUSEKEEPING.*OVERLOAD|HOUSEKEEPING.*BACKLOG/i, 'ROOM READINESS'],
  [/HOUSEKEEPING/i,      'HOUSEKEEPING'],
  [/MAINTENANCE/i,       'MAINTENANCE'],
  [/RESPONSE.*DELAY|SERVICE.*DELAY/i, 'GUEST EXPERIENCE'],
  [/REQUEST.*SPIKE|REQUEST.*SURGE/i, 'FRONT DESK'],
  [/REQUEST|SERVICE/i,   'ROOM SERVICE'],
  [/SLA/i,               'OPERATIONS'],
  [/WORKLOAD|CAPACITY/i, 'OPERATIONS'],
  [/GUEST|EXPERIENCE/i,  'GUEST EXPERIENCE'],
  [/CHECK.?IN|CHECK.?OUT|FRONT/i, 'FRONT DESK'],
];

export function getServiceImpact(type: string): ServiceImpact {
  const t = (type ?? '').toUpperCase();
  for (const [pattern, impact] of TYPE_IMPACT_MAP) {
    if (pattern.test(t)) return impact;
  }
  return 'OPERATIONS';
}

export function getImpactStyle(impact: ServiceImpact): { bg: string; text: string } {
  return IMPACT_STYLES[impact];
}
